<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit">
              {{ $t('Sales invoices') }}
            </div>
          </template>
          <template #end>
            <Button v-if="isFocusMode"
                    :icon="'pi pi-bars'"
                    :label="$t('Show all')"
                    @click="exitFocusedMode"
                    class="p-button-raised"/>
            <template v-else>
              <div class="p-d-flex p-ai-center p-mr-2" v-if="$store.getters.isAccountantOrHigher">
                <Checkbox :disabled="dataTableIsLoading" @change="getItems(true)" id="sales-invoices-show-only-unpaid-inputswitch" :binary="true" v-model="showOnlyUnpaid"/>
                <div>
                  <label for="sales-invoices-show-only-unpaid-inputswitch">
                    <div class="pointer p-p-1">{{ $t('showOnlyUnpaidLabel') }}</div>
                  </label>
                </div>
              </div>
              <DatesRangeCalendar :disabled="dataTableIsLoading"
                                  ref="datesRangeCalendar"
                                  :isEmptyByDefault="true"
                                  @change-dates-range="changeDatesRange"
                                  class="p-mr-2"/>
              <SearchInput :disabled="dataTableIsLoading"
                           @change-processed-search-value="changeProcessedSearchValue"
                           v-model="searchData"
                           @show-spinner="isLoading = true"/>
            </template>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
                {name: 'number', title: $t('Number'), sortable: true},
                {name: 'export_number', icon: 'ti-link', tooltip: $t('accountingExportColumnTooltip'), sortable: false, isHidden: !showAccountingExportColumn},
                {name: 'order', title: $t('Order'), sortable: false},
                {name: 'customer', title: $t('Customer'), sortable: false},
                {name: 'issued', title: $t('issuedLabel'), sortable: true},
                {name: 'payment_due', title: $t('paymentDueLabel'), sortable: false},
                {name: 'overdue', title: '', sortable: false},
                {name: 'grand_total', title: $t('Sum'), sortable: false},
                {name: 'unpaid', title: $t('Due'), sortable: false},
                {name: 'method', title: $t('paymentMethodShortLabel'), sortable: false},
                {name: 'state', title: $t('Status'), sortable: false},
                {name: '', title: null, sortable: false},
                {name: 'message_icon', title: null, sortable: false},
                {name: 'sent_at', title: null, sortable: false},
                {name: 'created_at', title: $t('Created'), sortable: true},
            ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span>{{ item.number }}</span>
              </td>
              <td v-if="showAccountingExportColumn">
<!--                <div v-if="item.export_number || item.export_id">-->
<!--                  <i class="ti-link" v-tooltip.top="item.export_number"/>-->
<!--                  <ChangedAfterExportIcon :show="item.changed_after_export" size="1.05rem" top="-1px"/>-->
<!--                </div>-->
                <div v-if="item.export_number || item.export_id">
                  <i class="ti-link" v-tooltip.top="getAccountingExportTooltip(item)"/>
                  <ChangedAfterExportIcon :show="item.changed_after_export" size="1.05rem" top="-1px"/>
                </div>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <router-link v-if="item.order"
                             :to="{ path: `/orders/${item.order.uid}` }"
                             :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                             rel="noopener"
                             draggable="false">
                  {{ item.order.number }}
                </router-link>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <CustomerData :user="item.customer"/>
              </td>

              <td>
                <span v-if="item.issued">{{ formatDay(item.issued) }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.payment_due"
                      :class="{'warning-color': item.payment_due && (countOverdueDays(item.payment_due) < 0) && item.state !== 5 }">
                  {{ formatDay(item.payment_due) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.payment_due && item.state !== 5"
                      :class="{'warning-color': countOverdueDays(item.payment_due) < 0 }">
                  {{ countOverdueDays(item.payment_due) }}
                </span>
              </td>
              <td>
                <span>{{ formatCurrency(item.grand_total) }}</span>
              </td>
              <td>
<!--                <span :class="{'warning-color': item.payment_due && (countOverdueDays(item.payment_due) <= 0) && item.state !== 5 }">{{ formatCurrency(item.unpaid) }}</span>-->
                <span>{{ formatCurrency(item.unpaid) }}</span>
              </td>
              <td>
                <i v-for="(method, index) of generatePaymentsMethods(item.payments)" :key="index"
                   v-tooltip.top="getPaymentMethodTooltip(method)"
                   class="p-p-1"
                   :class="{
                  'ti-wallet': method === 1,
                  'ti-credit-card': method === 3,
                  'ti-exchange-vertical vg-icon-rotate-90': method === 5
                }"/>
              </td>

              <td>
                <div  class="p-d-flex p-flex-wrap p-ai-center">
                  <div class="state-badge"
                       :class="InvoiceStates.find(state => state.num === item.state)?.color || ''">
                    {{ InvoiceStates.find(state => state.num === item.state)?.label[$i18n.locale] || '' }}
                  </div>
                </div>
              </td>

              <td>
                <DownloadPdfButton :item="item" isSmall type="sales"/>
              </td>

              <td>
                <EmailSendButton :item="item" @create-email="createSalesInvoiceEmail"/>
              </td>

              <td>
                <div v-if="item.sent_at" class="p-d-flex p-jc-center p-ai-center p-p-2">
                  <i style="font-size: 1.1em" class="ti-email p-text-secondary" v-tooltip.top="$t('statusSent') + ': ' + formatDate(item.sent_at)"/>
                </div>
              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td v-show="actionButtonsCount">
                <div class="actions">
                  <EditButton v-show="canManage"
                              :disabled="!showModals || disableEditButton || !allDataLoaded || dataTableIsLoading"
                              @click="editItem(item)"/>

                  <span v-show="canManage"
                        v-tooltip.left="{
                          value: $t('financialPeriodClosedTooltip'),
                          disabled: canDeleteInvoice(item)
                        }">
                    <DeleteButton @click="confirmItemDelete(item)"
                                  :disabled="!showModals || dataTableIsLoading || !canDeleteInvoice(item)"/>
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <InvoiceModal v-if="showModals"
                      :visible="itemModal"
                      :item="item"
                      :masters="masters"
                      :responsibles="responsibles"
                      :taxes="taxes"
                      :services="services"
                      :suppliers="suppliers"
                      mode="sales"
                      @update-items="updateItems"
                      @close="closeItemModal"/>

        <ConfirmDeleteModal v-if="showModals"
                            :modalDataIsLoading="modalDataIsLoading"
                            :visible="confirmItemDeleteModal"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="itemToDeleteData"/>

        <EmailModal v-if="showModals"
                    :visible="emailModal"
                    :item="emailData"
                    @email-sent="emailOnSent"
                    @close="closeEmailModal"/>
			</div>
		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import InvoiceStates from '@/translations/states/InvoiceStates'
import InvoiceModal from '@/pages/finance/components/InvoiceModal.vue'
import formatMixins from '@/mixins/formatMixins'
import calculateMixins from '@/mixins/calculateMixins'
import httpMixins from '@/mixins/httpMixins'
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import EmailMessages from "@/translations/EmailMessages";
import EmailModal from "@/pages/messages/components/EmailModal";
import DownloadPdfButton from "@/components/DownloadPdfButton.vue";
import EmailSendButton from "@/components/EmailSendButton.vue";
import templateLanguageMixin from '@/mixins/templateLanguageMixin'
import CustomerData from "@/components/CustomerData.vue";
import ChangedAfterExportIcon from "@/components/ChangedAfterExportIcon.vue";

export default {
  mixins: [ formatMixins, calculateMixins, httpMixins, datatableMixins, showErrorsMixins, templateLanguageMixin ],
  components: {
    CustomerData,
    DownloadPdfButton,
    InvoiceModal,
    DatesRangeCalendar,
    EmailModal,
    EmailSendButton,
    ChangedAfterExportIcon
  },
	data() {
		return {
      emailModal: false,
      emailData: {},
      showOnlyUnpaid: false,

      fieldsExpand: '' +
          'accounting_provider_name,' +
          'customer,' +
          'customer.customerStatus,' +
          'contactPerson,' +
          'car,order,' +
          'creator,' +
          'order_positions,' +
          'order_positions.historyCount,' +
          // 'order_positions.request_state_history,' +
          'order_positions.requestStateHistoryCount,' +
          'order_consumable,' +
          'prepaymentInvoicePositions.tax,' +
          'prepaymentInvoicePositions.creator,' +
          // 'prepaymentInvoice,' +
          'payments,' +
          'sent_by',
      apiLink: 'sales-invoice',
      deleteItemUrl: 'sales-invoice/delete',
      routePath: '/sales-invoices',
      sort: {
        column: 'issued',
        direction: '-',
      },
      defaultSort: {
        column: 'issued',
        direction: '-',
      },
      InvoiceStates,
		}
	},
  watch: {
    '$store.state.firstLayerIsOpened'(value) {
      if (!value) {
        if (this.emailModal) {
          this.closeEmailModal()
        }
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.getItems()
    },
    emailOnSent(data) {
      if (!data) return
      // console.log(data)
      const item = this.items?.find(i => i.id === data.itemId)
      if (item) {
        item.sent_at = data.sent_at
        item.sent_by = data.sent_by
        this.updateItems(data.itemId, false)
      }
    },
    createSalesInvoiceEmail({ item: salesInvoice, customerEmail }) {
      // if (!salesInvoice || !salesInvoice.customer) return false
      if (!salesInvoice) return false

      const customer = salesInvoice.customer
      const contactPerson = salesInvoice.contactPerson || null
      const recipient = this.resolveInvoiceRecipient(customer, contactPerson)

      const salesInvoiceMessage = this.resolveTemplateMessage(
          EmailMessages.salesInvoice,
          {
            recipientType: recipient.recipientType,
            customer,
            contactPerson,
          }
      )

      this.emailData = {
        isInvoice: true,
        sales_invoice_id: salesInvoice.id,
        subject: (salesInvoiceMessage?.subject || '') + salesInvoice.number,
        invoice_text: salesInvoiceMessage?.text || '',
        sent_at: salesInvoice.sent_at,
        file_path: salesInvoice.file_path,
        user_id: salesInvoice.user_id,
        to_email: recipient.toEmail || customerEmail || '',
      }

      this.emailModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeEmailModal() {
      this.emailData = {}
      this.emailModal = false
      // this.submitted = false
      this.$store.commit('toggleFirstLayer', false)
    },
    checkFinancialPeriodHasBeenClosed(issued) {
      const issuedTs = Number(issued || 0)
      const lockTs = Number(this.$store.getters.locationData?.financial_period_locked_until || 0)

      return issuedTs > 0 && lockTs > 0 && issuedTs <= lockTs
    },

    canDeleteInvoice(invoice) {
      if (!invoice?.id) return false
      if (!this.$store.getters.isAccountantOrHigher) return false
      if (!this.allDataLoaded) return false
      if (this.dataIsSending) return false

      if (this.checkFinancialPeriodHasBeenClosed(invoice.issued)) {
        return false
      }

      return true
    },
    generatePaymentsMethods(payments) {
      if (!payments) return false
      let paymentsMethods = []
      payments.forEach(payment => {
        if (paymentsMethods.indexOf(payment.type) === -1) {
          paymentsMethods.push(payment.type)
        }
      })
      return paymentsMethods
    },
    getPaymentMethodTooltip(method) {
      const tooltips = {
        1: this.$t('cashPaymentTooltip'),
        3: this.$t('cardPaymentTooltip'),
        5: this.$t('bankTransferTooltip'),
      }

      return tooltips[method] || ''
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner || this.itemNotFound) this.isLoading = true

      // console.log('get sales invoices')
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      const ctrl = this.setController('salesInvoices')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.isFocusMode ? null : (this.processedSearchValue ?? null),
            expand: this.fieldsExpand,
            show_only_unpaid: this.isFocusMode ? 0 : +this.showOnlyUnpaid,
            show: this.$route.query.show || null,
          },
          data: {
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal,
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }

          if (this.$route.query.show && (data.length === 0)) {
            this.showItemNotFoundError()
            this.itemNotFound = true
            return
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError?.(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (this._abortors?.salesInvoices === ctrl) {
          this._abortors.salesInvoices = null
        }
      }
    },
	},
  computed: {
    allDataLoaded() {
      return true
    },
    canManage() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canManage ? 2 : 0

    },
    itemToDeleteData() {
      return  this.itemToDelete?.number ?? null
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}
</style>
